import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChatContext } from '../contexts/ChatContext';
import api from '../utils/api';

const RARITY_COLOR = {
  bronze: '#b45309', silver: '#94a3b8', golden: '#f59e0b', platinum: '#0ea5e9',
};

function timeAgo(d, lang) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return lang === 'en' ? 'just now' : 'recién';
  if (min < 60) return lang === 'en' ? `${min}m` : `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return lang === 'en' ? `${h}h` : `${h}h`;
  const days = Math.floor(h / 24);
  return lang === 'en' ? `${days}d` : `${days}d`;
}

export default function TeamActivityTicker() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const chat = useChatContext();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  // Initial load — last 24h of non-common grants + battles across the team
  useEffect(() => {
    if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return;
    Promise.all([
      api.get('/stickers/team/recent').catch(() => ({ data: { grants: [] } })),
      api.get('/battles/recent').catch(() => ({ data: { battles: [] } })),
    ]).then(([stickersRes, battlesRes]) => {
      const stickers = (stickersRes.data?.grants || []).map(g => ({ ...g, kind: 'sticker' }));
      const battles = (battlesRes.data?.battles || []).map(b => ({ ...b, kind: 'battle' }));
      const merged = [...stickers, ...battles].sort((a, b) =>
        new Date(b.at).getTime() - new Date(a.at).getTime()
      ).slice(0, 20);
      setItems(merged);
    });
  }, [user]);

  // Live socket subscription on the chat namespace — pushed by stickerService.emitGrant
  useEffect(() => {
    const s = chat?.socketRef?.current;
    if (!s) return;

    const stickerHandler = (payload) => {
      // Skip self — own grants already get the personal toast
      if (payload.user_id === user?.id) return;
      setItems(prev => [{
        kind: 'sticker',
        user_id: payload.user_id,
        first_name: payload.user_first_name || '',
        key: payload.sticker.key,
        name: payload.sticker.name,
        emoji: payload.sticker.emoji,
        rarity: payload.sticker.rarity,
        unlocks_pokemon: payload.sticker.unlocks_pokemon,
        at: payload.at,
        count: payload.count,
        level_up: payload.level_up,
        _fresh: true,
      }, ...prev].slice(0, 20));
    };

    const battleHandler = (payload) => {
      setItems(prev => [{
        kind: 'battle',
        id: payload.id,
        winner_user_id: payload.winner_user_id,
        winner_first_name: payload.winner_first_name || '',
        winner_slug: payload.winner_slug,
        loser_user_id: payload.loser_user_id,
        loser_first_name: payload.loser_first_name || '',
        loser_slug: payload.loser_slug,
        at: payload.at,
        _fresh: true,
      }, ...prev].slice(0, 20));
    };

    s.on('sticker:granted:team', stickerHandler);
    s.on('battle:result', battleHandler);
    return () => {
      s.off('sticker:granted:team', stickerHandler);
      s.off('battle:result', battleHandler);
    };
  }, [chat?.socketRef?.current, user?.id]); // eslint-disable-line

  if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return null;
  if (items.length === 0) return null;

  return (
    <div className="card" style={{ height: '100%', marginBottom: 0 }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setCollapsed(v => !v)}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={15} /> {t('dashboard.teamActivity.title')}
          <span style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 400 }}>
            ({items.length})
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
          {collapsed ? t('common.expand') : t('common.collapse')}
        </div>
      </div>

      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
          {items.map((it, i) => {
            if (it.kind === 'battle') {
              return (
                <div key={`b-${it.id || i}-${it.at}`} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 6px',
                  fontSize: 12,
                  borderRadius: 4,
                  background: it._fresh ? 'rgba(245, 158, 11, 0.10)' : 'transparent',
                  transition: 'background 600ms ease',
                }}>
                  <span style={{ fontSize: 16, lineHeight: 1, width: 20, textAlign: 'center' }}>⚔️</span>
                  <span style={{ flex: 1, color: 'var(--c-text)' }}>
                    <strong>{it.winner_first_name}</strong>
                    {' '}{t('dashboard.teamActivity.beat')}{' '}
                    <strong>{it.loser_first_name}</strong>
                    <span style={{ color: 'var(--c-text-muted)' }}> · {it.winner_slug} vs {it.loser_slug}</span>
                  </span>
                  <span style={{ color: 'var(--c-text-muted)', fontSize: 11, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {timeAgo(it.at, i18n.language)}
                  </span>
                </div>
              );
            }
            const color = RARITY_COLOR[it.rarity] || RARITY_COLOR.bronze;
            return (
              <div key={`s-${it.user_id}-${it.key}-${it.at}-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 6px',
                fontSize: 12,
                borderRadius: 4,
                background: it._fresh ? `${color}14` : 'transparent',
                transition: 'background 600ms ease',
              }}>
                <span style={{ fontSize: 16, lineHeight: 1, width: 20, textAlign: 'center' }}>
                  {it.emoji || '🎖️'}
                </span>
                <span style={{ flex: 1, color: 'var(--c-text)' }}>
                  <strong>{it.first_name}</strong> {t('dashboard.teamActivity.earned')}{' '}
                  <strong style={{ color }}>{it.name}</strong>
                  {it.count > 1 && <span style={{ color: 'var(--c-text-muted)' }}> ×{it.count}</span>}
                  {it.unlocks_pokemon && (
                    <span style={{ color: 'var(--c-text-muted)' }}> · 🔓 {it.unlocks_pokemon}</span>
                  )}
                </span>
                <span style={{ color: 'var(--c-text-muted)', fontSize: 11, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {timeAgo(it.at, i18n.language)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
